import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Breadcrumb } from '@/components/ui/Breadcrumb';
import { Download } from 'lucide-react';

const HEADINGS = [
  { id: 'a-theatre-team-that-works-hard-and-still-pulls-apart', label: 'A theatre team that works hard and still pulls apart' },
  { id: 'three-ways-of-deciding-in-the-same-room', label: 'Three ways of deciding in the same room' },
  { id: 'four-steps', label: 'Four steps' },
  { id: 'decide-what-success-looks-like-before-you-start', label: 'Decide what success looks like before you start' },
];

export function ScenarioHealthcareSystemsPage() {
  const [activeId, setActiveId] = useState<string>('');

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
          }
        });
      },
      { rootMargin: '-20% 0px -60% 0px' }
    );

    HEADINGS.forEach(({ id }) => {
      const element = document.getElementById(id);
      if (element) {
        observer.observe(element);
      }
    });

    return () => observer.disconnect();
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const y = element.getBoundingClientRect().top + window.scrollY - 100;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  return (
    <main className="pt-32 pb-24 min-h-screen bg-white relative">
      <Helmet>
        <title>Healthcare Systems Optimisation: An Illustrative Scenario | FIVE LENS Enneagram</title>
        <meta name="description" content="An illustrative scenario showing how the Energy Centres lens can help a multidisciplinary clinical team understand recurring friction, what to try, and what to measure." />
        <meta property="og:title" content="Healthcare Systems Optimisation: An Illustrative Scenario | FIVE LENS Enneagram" />
        <meta property="og:description" content="An illustrative scenario showing how the Energy Centres lens can help a multidisciplinary clinical team understand recurring friction, what to try, and what to measure." />
        <link rel="canonical" href="https://www.fivelensenneagram.com/resource-library/healthcare-systems-optimisation" />
      </Helmet>

      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row gap-16 items-start mt-8">
          <aside className="hidden lg:block w-72 shrink-0 sticky top-32 max-h-[calc(100vh-8rem)] overflow-y-auto scrollbar-hide pr-4">
            <div className="mb-4 text-xs font-bold uppercase tracking-widest text-ink/70">
              Contents
            </div>
            <nav className="flex flex-col gap-3 border-l-2 border-ground-deep">
              {HEADINGS.map(({ id, label }) => (
                <button
                  key={id}
                  onClick={() => scrollToSection(id)}
                  className={`text-left text-sm py-1 pl-4 border-l-2 -ml-[2px] transition-colors ${
                    activeId === id 
                      ? 'border-rule-3 text-ink font-bold' 
                      : 'border-transparent text-ink/70 hover:text-ink'
                  }`}
                >
                  {label}
                </button>
              ))}
            </nav>
          </aside>

          <article className="flex-1 max-w-3xl min-w-0">
            <div className="mb-8">
              <Breadcrumb items={[
                { label: 'Resource Library', href: '/resource-library' },
                { label: 'Healthcare Systems Optimisation' }
              ]} />
            </div>

            {/* Illustrative Scenario Top Badge */}
            <div className="mb-6 inline-block bg-ground-alt border border-ground-deep text-ink/80 text-xs font-semibold px-4 py-2 rounded-md">
              Illustrative scenario: a fictional composite, not a client case study.
            </div>

            <div className="prose prose-lg prose-headings:font-sans prose-h1:font-serif prose-h2:text-ink prose-h3:text-ink prose-h4:text-ink prose-a:text-rule-3 text-ink/70 article-content">

              {/* Hero Banner */}
              <div className="bg-gradient-to-br from-ink to-ink/80 text-white p-12 rounded-lg mb-16 relative overflow-hidden">
                <div>
                  <div className="mark text-white">FIVE LENS<span className="sub text-white/70">ENNEAGRAM</span></div>
                  <div className="tagline text-white/80">Developing People &amp; Expanding Possibilities</div>

                  <div className="kicker text-rule-3">Illustrative Scenario</div>
                  <div className="flex flex-col md:flex-row md:justify-between md:items-end gap-6 mb-8">
                    <div>
                      <h1 className="!text-white !mb-4">Healthcare Systems<br />Optimisation</h1>
                      <div className="deck !mb-0 max-w-2xl text-white/90">
                        An illustrative scenario showing how the Energy Centres lens can help a multidisciplinary clinical team understand recurring friction, what to try, and what to measure.
                      </div>
                    </div>
                    <a 
                      href="/downloads/Healthcare-Systems-Optimisation-Illustrative-Scenario.pdf" 
                      download 
                      className="inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-rule-3 disabled:pointer-events-none disabled:opacity-50 border border-ground-deep bg-ground/10 hover:bg-ground/20 text-ground shadow-sm h-10 px-4 py-2 shrink-0 no-underline"
                    >
                      <Download className="mr-2 h-4 w-4" />
                      Download PDF
                    </a>
                  </div>
                  <div className="rule5">
                    <span style={{"background":"var(--rule-3)"}}></span>
                    <span style={{"background":"var(--rule-5)"}}></span>
                    <span style={{"background":"var(--rule-3)"}}></span>
                    <span style={{"background":"var(--rule-2)"}}></span>
                    <span style={{"background":"var(--rule-1)"}}></span>
                  </div>
                </div>
              </div>

              {/* Section: The Situation */}
              <section className="mb-16">
                <div className="border border-ground-deep border-t-4 border-t-ink bg-ground-alt/30 p-6 mb-8 rounded-b-md shadow-sm text-sm">
                  <p className="m-0"><b>This is an illustrative scenario, not a case study.</b> The organisation, team and events below are fictional. They are a composite of patterns that FIVE LENS practitioners commonly encounter in clinical and other high-pressure teams. No real client is described, and no outcomes are claimed. The purpose is to show how the framework is applied, step by step, so you can judge whether it fits your own context.</p>
                </div>

                <div className="border-b-2 border-ink pb-4 mb-8">
                  <div className="text-xs uppercase tracking-widest text-ink/70 font-bold mb-2 block">The situation</div>
                  <h2 id="a-theatre-team-that-works-hard-and-still-pulls-apart">A theatre team that works hard and still pulls apart</h2>
                </div>
                <p className="lead">Imagine the surgical directorate of a regional hospital group. Its theatre team is skilled, committed and tired. Lists start late, handovers are tense, and the same arguments recur every week.</p>
                <p>The team includes consultant surgeons, anaesthetists, theatre nurses and operating department practitioners, with an operations manager responsible for theatre utilisation. Each group is competent. Each, privately, sees one of the others as the reason the day goes wrong.</p>
                <p>The surgeons experience late starts as other people's lack of urgency. The nursing team experiences pressure to start before the room is truly ready, and feels unheard when they raise concerns. The operations manager sees a utilisation dashboard that never improves, and responds with more process and more data requests, which both clinical groups experience as paperwork that takes them away from patients.</p>
                <p>The directorate has already tried the obvious fixes. A process redesign tightened the pre-operative checklist. A wellbeing app was offered to all staff. Both were sensible. Neither changed the weekly arguments, because neither touched the reason the arguments happen.</p>
              </section>

              {/* Section: The Lens */}
              <section className="mb-16">
                <div className="border-b-2 border-ink pb-4 mb-8">
                  <div className="text-xs uppercase tracking-widest text-ink/70 font-bold mb-2 block">The lens</div>
                  <h2 id="three-ways-of-deciding-in-the-same-room">Three ways of deciding in the same room</h2>
                </div>
                <p className="lead">The FIVE LENS Energy Centres lens describes three ways people process information and decide: through the instinctual centre, the emotional centre and the intellectual centre. Everyone uses all three. Under pressure, most people lean on one.</p>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 my-8">
                  <div className="border-t-4 border-t-rule-3 p-6 bg-ground-alt/30 rounded-b-md shadow-sm">
                    <div className="text-xs uppercase tracking-widest font-bold text-ink/70 mb-1">Instinctual</div>
                    <h4 className="text-lg font-bold text-ink mb-1">&quot;Let&apos;s go.&quot;</h4>
                    <div className="text-xs italic text-ink/80 mb-3">Presence, action and boundaries.</div>
                    <p className="text-sm text-ink/70 m-0">In this scenario, the surgeons lead from here. Decisive momentum is exactly what an emergency needs. Over-relied on, it reads as impatience and can steamroll legitimate concerns.</p>
                  </div>
                  <div className="border-t-4 border-t-rule-2 p-6 bg-ground-alt/30 rounded-b-md shadow-sm">
                    <div className="text-xs uppercase tracking-widest font-bold text-ink/70 mb-1">Emotional</div>
                    <h4 className="text-lg font-bold text-ink mb-1">&quot;Is everyone OK?&quot;</h4>
                    <div className="text-xs italic text-ink/80 mb-3">Relationship, impact and trust.</div>
                    <p className="text-sm text-ink/70 m-0">The nursing team leads from here. Reading the room and noticing who is struggling protects patients and colleagues. Over-relied on, it can avoid the hard conversation until it boils over.</p>
                  </div>
                  <div className="border-t-4 border-t-rule-5 p-6 bg-ground-alt/30 rounded-b-md shadow-sm">
                    <div className="text-xs uppercase tracking-widest font-bold text-ink/70 mb-1">Intellectual</div>
                    <h4 className="text-lg font-bold text-ink mb-1">&quot;What does the data say?&quot;</h4>
                    <div className="text-xs italic text-ink/80 mb-3">Analysis, planning and foresight.</div>
                    <p className="text-sm text-ink/70 m-0">The operations manager leads from here. Clarity and planning are what a complex service needs. Over-relied on, it produces reports that the people doing the work see as detached from reality.</p>
                  </div>
                </div>

                <p>Seen through this lens, the weekly argument is not three groups being difficult. It is three valid forms of intelligence, each applied with conviction, and each interpreting the others as the obstacle. The surgeon who wants to start is right that delay costs patients. The nurse who wants one more check is right that safety matters. The manager who wants the data is right that the service cannot improve what it cannot see.</p>

                <h3>The other lenses add depth</h3>
                <p><b>Social Drives.</b> High use of agency staff has weakened the nursing team's sense of belonging: the people they work beside change weekly. The consultants' drive for esteem and competence is frustrated by a service that does not run the way they know it could. The operations manager's drive for security is under pressure from targets they do not control. Each threatened need contracts behaviour into self-protection.</p>
                <p><b>Emotional Resilience.</b> The lens describes a cycle of react, recover and adapt. In this team, there is no recovery stage at all. One list runs into the next, debriefs are skipped, and the strain of a difficult case is carried into the following one. Reactivity rises not because people lack resilience, but because the system gives them no room to use it.</p>
              </section>

              {/* Section: What a practitioner would do */}
              <section className="mb-16">
                <div className="border-b-2 border-ink pb-4 mb-8">
                  <div className="text-xs uppercase tracking-widest text-ink/70 font-bold mb-2 block">What a practitioner would do</div>
                  <h2 id="four-steps">Four steps</h2>
                </div>
                <ol className="space-y-4 my-6 list-decimal list-inside pl-2">
                  <li className="pl-2"><b>Profile the team.</b> Each member completes the FIVE LENS assessment. The results are shared with the individual first, in a one-to-one debrief, before anything is shared with the group.</li>
                  <li className="pl-2"><b>Run a Team Discovery Day.</b> In a facilitated session, the team sees its collective map: which centres dominate, which needs are under threat, and where resilience is being drawn down. The point is not to label people, but to give the team a shared, neutral language for what has been happening.</li>
                  <li className="pl-2"><b>Agree three working practices.</b> The team designs its own practices, using that language. In a scenario like this one, they might include a two-minute pre-list huddle that deliberately touches all three centres: <i>What is the plan?</i> (intellectual), <i>Who has a concern?</i> (emotional), <i>What is the call?</i> (instinctual). They might add a protected five-minute recovery point after complex cases, and a simple phrase any member can use to pause and be heard without it becoming a confrontation.</li>
                  <li className="pl-2"><b>Review and adjust.</b> Through a Team Performance Process, the practitioner returns over the following months to review what is working, what has slipped back under pressure, and what to adjust.</li>
                </ol>
                <div className="border border-ground-deep bg-ground-alt/30 p-6 my-6 text-sm rounded-md">
                  <p className="m-0">Nothing here replaces clinical governance, safety checklists or established escalation routes. The practices above sit alongside them. Their purpose is to change how people talk to each other while using those processes, not to change the processes themselves.</p>
                </div>
              </section>

              {/* Section: What to measure */}
              <section className="mb-16">
                <div className="border-b-2 border-ink pb-4 mb-8">
                  <div className="text-xs uppercase tracking-widest text-ink/70 font-bold mb-2 block">What to measure</div>
                  <h2 id="decide-what-success-looks-like-before-you-start">Decide what success looks like before you start</h2>
                </div>
                <p className="lead">Because this is a scenario, we report no results. If you ran something similar, these are indicators worth baselining before you begin, so you can see honestly whether anything changed.</p>

                <div className="overflow-x-auto my-6">
                  <table className="min-w-full text-left text-sm border-collapse">
                    <thead>
                      <tr className="border-b-2 border-ink">
                        <th className="py-2 pr-4 font-bold">Indicator</th>
                        <th className="py-2 font-bold">Why it matters</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b border-ground-deep"><td className="py-3 pr-4 font-bold align-top">First-case on-time starts</td><td className="py-3 align-top">A visible, routinely collected measure of whether the team is starting the day together.</td></tr>
                      <tr className="border-b border-ground-deep"><td className="py-3 pr-4 font-bold align-top">Staff sickness absence and turnover</td><td className="py-3 align-top">Lagging indicators of strain and belonging. Expect them to move slowly, if at all, within a year.</td></tr>
                      <tr className="border-b border-ground-deep"><td className="py-3 pr-4 font-bold align-top">Staff survey items on speaking up</td><td className="py-3 align-top">Whether people feel able to raise a concern and be heard. Ask the same questions before and after.</td></tr>
                      <tr className="border-b border-ground-deep"><td className="py-3 pr-4 font-bold align-top">Incident reporting rates</td><td className="py-3 align-top">Treat with care: a rise can mean people feel safer reporting, not that care has become less safe.</td></tr>
                      <tr className="border-b border-ground-deep"><td className="py-3 pr-4 font-bold align-top">The team's own rating</td><td className="py-3 align-top">A short, repeated question to the team: how well are we working together this month, from 1 to 10?</td></tr>
                    </tbody>
                  </table>
                </div>

                <h3>Questions for your own team</h3>
                <ul className="space-y-2 my-4 list-disc list-inside">
                  <li>When your team argues, is it really about the decision, or about how the decision should be made?</li>
                  <li>Which of the three centres does your team lean on most under pressure? Which does it neglect?</li>
                  <li>Is there any recovery time built into your working day, or does strain carry straight into the next task?</li>
                  <li>Whose needs are most under threat right now: security, belonging, esteem or meaning?</li>
                </ul>
              </section>

              {/* Call to action footer */}
              <div className="border border-ground-deep border-t-4 border-t-ink bg-ground-alt/30 p-8 my-12 rounded-b-md shadow-sm">
                <h3 className="!mt-0">Download the full scenario</h3>
                <p className="mb-6 text-sm">
                  Get the complete printable PDF scenario including facilitation questions and energy center diagnostics.
                </p>
                <div className="flex flex-wrap gap-4">
                  <a 
                    href="/downloads/Healthcare-Systems-Optimisation-Illustrative-Scenario.pdf" 
                    download 
                    className="inline-flex items-center justify-center px-6 py-3 bg-ink text-white font-bold text-xs uppercase tracking-widest hover:bg-rule-3 transition-colors no-underline"
                  >
                    <Download className="mr-2 h-4 w-4" /> Download Scenario PDF
                  </a>
                </div>
              </div>

            </div>
          </article>
        </div>
      </div>
    </main>
  );
}
